import React, { useEffect, useRef, useState } from "react";
import logger from "../lib/logger";
import { Btn, Eyebrow } from "../components/Primitives";

const MAX_SECONDS = 30;

export default function VideoRecord({ prompts, initialPrompt, onCapture, onCancel, existingUrl, existingPrompt }) {
    const videoRef = useRef(null);
    const previewRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);

    const [prompt, setPrompt] = useState(existingPrompt || initialPrompt || (prompts?.[0] || ""));
    const [stage, setStage] = useState(existingUrl ? "preview" : "idle"); // idle | recording | preview
    const [seconds, setSeconds] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(existingUrl || null);
    const [error, setError] = useState("");

    const setupCamera = async () => {
        setError("");
        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 960 } },
                audio: true,
            });
            streamRef.current = s;
            if (videoRef.current) {
                videoRef.current.srcObject = s;
                await videoRef.current.play();
            }
        } catch (e) {
            setError("Camera/mic access denied. Please enable and try again.");
        }
    };

    useEffect(() => {
        if (!existingUrl) setupCamera();
        return () => stopAll();
        // eslint-disable-next-line
    }, []);

    const stopAll = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            try { recorderRef.current.stop(); } catch (err) { logger.warn("recorder stop failed:", err); }
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    };

    const startRecording = () => {
        if (!streamRef.current) return;
        chunksRef.current = [];
        const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
            ? "video/webm;codecs=vp9,opus"
            : (MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "");
        try {
            const mr = mimeType ? new MediaRecorder(streamRef.current, { mimeType }) : new MediaRecorder(streamRef.current);
            recorderRef.current = mr;
            mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType || "video/webm" });
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url);
                setStage("preview");
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(t => t.stop());
                    streamRef.current = null;
                }
            };
            mr.start();
            setStage("recording");
            setSeconds(0);
            timerRef.current = setInterval(() => {
                setSeconds(s => {
                    if (s + 1 >= MAX_SECONDS) {
                        stopRecording();
                        return MAX_SECONDS;
                    }
                    return s + 1;
                });
            }, 1000);
        } catch (e) {
            setError("Recording failed. Try a different browser.");
        }
    };

    const stopRecording = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        }
    };

    const retake = () => {
        if (previewUrl && previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        chunksRef.current = [];
        setStage("idle");
        setSeconds(0);
        setupCamera();
    };

    const confirm = async () => {
        if (!chunksRef.current.length) {
            onCancel();
            return;
        }
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        onCapture(blob, prompt);
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="video-record-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button
                    data-testid="video-cancel"
                    onClick={() => { stopAll(); onCancel(); }}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">
                    Video · {MAX_SECONDS}s max
                </span>
            </div>

            <div className="px-7 pt-3">
                <Eyebrow>Your prompt</Eyebrow>
                <select
                    data-testid="video-prompt-select"
                    value={prompt}
                    disabled={stage !== "idle"}
                    onChange={e => setPrompt(e.target.value)}
                    className="mt-2 w-full bg-cream border border-burgundy/20 focus:border-burgundy p-3 font-display text-base text-burgundy focus:outline-none disabled:opacity-60"
                >
                    {(prompts && prompts.length ? prompts : [prompt]).map(p => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
            </div>

            <div className="flex-1 px-6 py-5 flex flex-col items-center">
                <div className="w-full aspect-[3/4] bg-burgundy/5 border border-burgundy/15 overflow-hidden relative" data-testid="video-stage">
                    {previewUrl ? (
                        <video ref={previewRef} src={previewUrl} controls playsInline className="w-full h-full object-cover" data-testid="video-preview" />
                    ) : (
                        <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
                    )}
                    {stage === "recording" && (
                        <div className="absolute top-3 left-3 flex items-center gap-2 bg-burgundy text-cream px-2.5 py-1 text-[10px] tracking-[0.28em] uppercase" data-testid="video-recording-indicator">
                            <span className="w-2 h-2 rounded-full bg-cream animate-pulse" />
                            Rec · {seconds}s
                        </div>
                    )}
                </div>
                {error && <div data-testid="video-error" className="mt-3 text-sm text-burgundy">{error}</div>}
            </div>

            <div className="px-6 pb-8 space-y-2.5">
                {stage === "idle" && (
                    <Btn data-testid="video-record-btn" onClick={startRecording}>
                        Start recording
                    </Btn>
                )}
                {stage === "recording" && (
                    <Btn data-testid="video-stop-btn" onClick={stopRecording}>
                        Stop · {MAX_SECONDS - seconds}s left
                    </Btn>
                )}
                {stage === "preview" && (
                    <>
                        <Btn data-testid="video-confirm-btn" onClick={confirm}>
                            Use this video
                        </Btn>
                        <Btn data-testid="video-retake-btn" variant="secondary" onClick={retake}>
                            Retake
                        </Btn>
                    </>
                )}
            </div>
        </div>
    );
}
